import { forwardRef, type HTMLAttributes, type TdHTMLAttributes, type ThHTMLAttributes } from 'react';

import { cn } from '../cn';

/**
 * Table primitives. Tables have square corners, horizontal rules (no zebra), a muted header with ink semibold
 * labels and a strong bottom rule, full-opacity hover/selected fills, and row height from the density setting.
 * Numeric cells: pass align="end" (right in English, left in Arabic).
 */
export const Table = forwardRef<HTMLTableElement, HTMLAttributes<HTMLTableElement>>(function Table({ className, ...props }, ref) {
  return <table ref={ref} className={cn('w-full border-separate border-spacing-0 text-table text-ink', className)} {...props} />;
});

export const THead = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement> & { sticky?: boolean }>(
  function THead({ className, sticky = false, ...props }, ref) {
    return <thead ref={ref} className={cn(sticky && 'sticky top-0 z-(--z-sticky)', className)} {...props} />;
  },
);

export const TBody = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(function TBody(props, ref) {
  return <tbody ref={ref} {...props} />;
});

export const TFoot = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(function TFoot({ className, ...props }, ref) {
  return <tfoot ref={ref} className={cn('font-semibold', className)} {...props} />;
});

export const TR = forwardRef<HTMLTableRowElement, HTMLAttributes<HTMLTableRowElement> & { selected?: boolean; interactive?: boolean }>(
  function TR({ className, selected, interactive, ...props }, ref) {
    return (
      <tr
        ref={ref}
        data-selected={selected || undefined}
        aria-selected={selected}
        className={cn(
          'group/row',
          interactive && 'hover:[&>td]:bg-row-hover',
          selected && 'edge-accent [&>td]:bg-row-selected [&>td:first-child]:edge-start',
          className,
        )}
        {...props}
      />
    );
  },
);

type Align = 'start' | 'end' | 'center';
const alignClass: Record<Align, string> = { start: 'text-start', end: 'text-end', center: 'text-center' };

export const TH = forwardRef<HTMLTableCellElement, Omit<ThHTMLAttributes<HTMLTableCellElement>, 'align'> & { align?: Align }>(function TH(
  { className, align = 'start', scope = 'col', ...props },
  ref,
) {
  return (
    <th
      ref={ref}
      scope={scope}
      className={cn(
        'h-(--row-header) border-b border-border-strong bg-surface-muted px-2.5 text-meta font-semibold whitespace-nowrap text-ink',
        alignClass[align],
        className,
      )}
      {...props}
    />
  );
});

export const TD = forwardRef<HTMLTableCellElement, Omit<TdHTMLAttributes<HTMLTableCellElement>, 'align'> & { align?: Align; numeric?: boolean }>(
  function TD({ className, align, numeric, ...props }, ref) {
    const a: Align = align ?? (numeric ? 'end' : 'start');
    return (
      <td
        ref={ref}
        className={cn(
          'h-(--row-height) border-b border-border bg-surface px-2.5 align-middle whitespace-nowrap',
          numeric && 'tabular',
          alignClass[a],
          className,
        )}
        {...props}
      />
    );
  },
);

/** Totals row cell: strong top rule, semibold. */
export const TotalTD = forwardRef<HTMLTableCellElement, Omit<TdHTMLAttributes<HTMLTableCellElement>, 'align'> & { align?: Align; numeric?: boolean }>(
  function TotalTD({ className, align, numeric, ...props }, ref) {
    const a: Align = align ?? (numeric ? 'end' : 'start');
    return (
      <td
        ref={ref}
        className={cn(
          'h-(--row-height) border-t border-border-strong bg-surface px-2.5 align-middle font-semibold whitespace-nowrap',
          numeric && 'tabular',
          alignClass[a],
          className,
        )}
        {...props}
      />
    );
  },
);

/**
 * Props for a horizontally scrolling table wrapper: a named region that keyboard users can
 * focus and scroll with the arrow keys (WCAG 2.1.1, axe scrollable-region-focusable).
 */
export function scrollRegion(label: string) {
  return { role: 'region', 'aria-label': label, tabIndex: 0 } as const;
}
